import {SpringPageable} from "@variocube/app-ui";
import {useMemo} from "react";
import {TypeOf} from "zod";
import {useAccessToken} from "../auth";
import {useClientId} from "../ClientIdProvider";
import {useTenantId} from "../tenant";
import {BookingSettings, CompartmentSettings, createApiClientWithBaseConfig, LocationType} from "./client";
import {GeoLocation, get_QueryLocations, LocationCreation, LocationMutation, SearchSettings} from "./client.gen";

export function useLocationApi() {
	const accessToken = useAccessToken();
	const tenantId = useTenantId();
	const clientId = useClientId();
	return useMemo(() => createLocationApi(tenantId, clientId, accessToken), [tenantId, accessToken]);
}

export type LocationQuery = TypeOf<get_QueryLocations["parameters"]>["query"];

export const LOCATION_TYPES = LocationType.options.map(option => option.value);

function createLocationApi(tenantId: string, clientId: string | undefined, accessToken: string | undefined) {
	const {api, withConfig} = createApiClientWithBaseConfig(clientId, accessToken);
	return {
		getLocation: (locationId: number) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}",
				withConfig({
					path: {tenantId, locationId},
				}),
			),
		getLocationChildren: (locationId: number, pageable: SpringPageable) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/children",
				withConfig({
					path: {tenantId, locationId},
					query: pageable,
				}),
			),
		updateLocation: (locationId: number, mutation: LocationMutation) =>
			api.put(
				"/api/tenants/{tenantId}/locations/{locationId}",
				withConfig({
					path: {tenantId, locationId},
					body: mutation,
				}),
			),
		deleteLocation: (locationId: number) =>
			api.delete(
				"/api/tenants/{tenantId}/locations/{locationId}",
				withConfig({
					path: {tenantId, locationId},
				}),
			),
		createLocation: (locationCreation: LocationCreation) =>
			api.post(
				"/api/tenants/{tenantId}/locations",
				withConfig({
					path: {tenantId},
					body: locationCreation,
				}),
			),
		queryLocations: (query: LocationQuery) =>
			api.get(
				"/api/tenants/{tenantId}/locations",
				withConfig({
					path: {tenantId},
					query,
				}),
			),
		getLocationTrees: () =>
			api.get(
				"/api/tenants/{tenantId}/location-trees",
				withConfig({
					path: {tenantId},
				}),
			),
		getLocationCompartments: (locationId: number, pageable: SpringPageable) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/compartments",
				withConfig({
					path: {tenantId, locationId},
					query: pageable,
				}),
			),
		getLocationAuditLog: (locationId: number, pageable: SpringPageable) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/audit",
				withConfig({
					path: {tenantId, locationId},
					query: pageable,
				}),
			),
		getLocationCompartmentTemplate: (locationId: number) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/compartments/template",
				withConfig({
					path: {tenantId, locationId},
				}),
			),
		getLocationSettings: (locationId: number) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/settings",
				withConfig({
					path: {tenantId, locationId},
				}),
			),
		getLocationBookingSettings: (locationId: number) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/settings/booking",
				withConfig({
					path: {tenantId, locationId},
				}),
			),
		updateLocationBookingSettings: (locationId: number, bookingSettings: BookingSettings) =>
			api.put(
				"/api/tenants/{tenantId}/locations/{locationId}/settings/booking",
				withConfig({
					path: {tenantId, locationId},
					body: bookingSettings,
				}),
			),
		getLocationCompartmentSettings: (locationId: number) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/settings/compartment",
				withConfig({
					path: {tenantId, locationId},
				}),
			),
		updateLocationCompartmentSettings: (locationId: number, compartmentSettings: CompartmentSettings) =>
			api.put(
				"/api/tenants/{tenantId}/locations/{locationId}/settings/compartment",
				withConfig({
					path: {tenantId, locationId},
					body: compartmentSettings,
				}),
			),
		getLocationGeoLocation: (locationId: number) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/geo-location",
				withConfig({
					path: {tenantId, locationId},
				}),
			),
		updateLocationGeoLocation: (locationId: number, geoLocation: GeoLocation) =>
			api.put(
				"/api/tenants/{tenantId}/locations/{locationId}/geo-location",
				withConfig({
					path: {tenantId, locationId},
					body: geoLocation,
				}),
			),
		getLocationSearchSettings: (locationId: number) =>
			api.get(
				"/api/tenants/{tenantId}/locations/{locationId}/search-settings",
				withConfig({
					path: {tenantId, locationId},
				}),
			),
		updateLocationSearchSettings: (locationId: number, searchSettings: SearchSettings) =>
			api.put(
				"/api/tenants/{tenantId}/locations/{locationId}/search-settings",
				withConfig({
					path: {tenantId, locationId},
					body: searchSettings,
				}),
			),
	};
}
